import React, { useEffect, useState } from "react";
import { C } from "../constants/colors";
import { getAssessmentBanks } from "../api";
import { UploadZone } from "./UploadZone";
import { FIELD_TONES, FieldGroup, FancyTextarea } from "./FormControls";

const BANK_GROUPS = [
  {
    title: "Core practice",
    hint: "Feeds coding, speaking, and case work every day",
    tone: "blue",
    icon: "ti-flame",
    items: [
      { key: "leetcode", label: "LeetCode Top 100", desc: "One unique problem per day — no repeats", tone: "amber", icon: "ti-code" },
      { key: "pm", label: "3C / PM Questions", desc: "Estimation, strategy, execution, design", tone: "blue", icon: "ti-message-2" },
      { key: "cases", label: "Business Cases", desc: "Different case per teammate for richer solutions", tone: "green", icon: "ti-briefcase" },
    ],
  },
  {
    title: "Career & placement",
    hint: "Short daily boosts — not long research blocks",
    tone: "amber",
    icon: "ti-trophy",
    items: [
      { key: "company", label: "Company Prep", desc: "Dream-company micro-prep (8–12 min)", tone: "coral", icon: "ti-building-skyscraper" },
      { key: "iae", label: "IAE / PYQ", desc: "Upload papers below to drive exam focus", tone: "green", icon: "ti-file-certificate" },
      { key: "certifications", label: "Certifications", desc: "Global / AI cert practice slots", tone: "teal", icon: "ti-certificate" },
    ],
  },
  {
    title: "Platforms",
    hint: "Optional guided slots inside your daily path",
    tone: "teal",
    icon: "ti-apps",
    items: [
      { key: "analyticsVidhya", label: "Analytics Vidhya", desc: "AI course progress in the schedule", tone: "green", icon: "ti-chart-dots" },
      { key: "agentWorkbench", label: "SNS Agent Workbench", desc: "n8n-style: build automation agents daily", tone: "amber", icon: "ti-robot" },
    ],
  },
];

export const defaultAssessmentConfig = {
  banks: {
    pm: true,
    leetcode: true,
    cases: true,
    iae: false,
    company: true,
    certifications: false,
    analyticsVidhya: false,
    agentWorkbench: true,
  },
  avCourseNotes: "",
  agentWorkbenchNotes: "",
  enrichFromLinks: false,
};

export default function AssessmentContentPanel({ config, onChange, files, setFiles }) {
  const [meta, setMeta] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getAssessmentBanks();
        if (!cancelled && res?.success) setMeta(res.data?.meta || null);
      } catch (_) { /* seed still used server-side */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const banks = config?.banks || defaultAssessmentConfig.banks;

  function toggleBank(key) {
    onChange({ ...config, banks: { ...banks, [key]: !banks[key] } });
  }

  function setField(key, value) {
    onChange({ ...config, [key]: value });
  }

  function setAllInGroup(items, on) {
    const next = { ...banks };
    items.forEach((i) => { next[i.key] = on; });
    onChange({ ...config, banks: next });
  }

  const enabledCount = Object.values(banks).filter(Boolean).length;
  const headerTone = FIELD_TONES.blue;

  return (
    <div>
      {/* Header */}
      <div style={{
        marginBottom: 16,
        padding: "16px 16px 14px",
        borderRadius: 14,
        background: `linear-gradient(135deg, ${headerTone.soft} 0%, ${FIELD_TONES.teal.soft} 50%, #FFFFFF 100%)`,
        border: `1.5px solid ${headerTone.accent}33`,
        boxShadow: `inset 3px 0 0 ${headerTone.accent}, 0 8px 24px ${headerTone.accent}12`,
      }}>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "flex-start",
          gap: 12, flexWrap: "wrap",
        }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <span style={{
                width: 34, height: 34, borderRadius: 10, background: "#fff",
                border: `1px solid ${headerTone.accent}44`, color: headerTone.accent,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
              }}>
                <i className="ti ti-stack-2" style={{ fontSize: 17 }} />
              </span>
              <div>
                <div style={{
                  fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase",
                  color: headerTone.chip,
                }}>
                  Path input
                </div>
                <p style={{ fontWeight: 800, fontSize: 16, color: C.text, margin: 0, letterSpacing: "-0.02em" }}>
                  Practice banks
                </p>
              </div>
            </div>
            <p style={{ fontSize: 13, color: C.muted, margin: 0, lineHeight: 1.5, maxWidth: 520 }}>
              Toggle what should appear in each day&apos;s path. Keep it light — enable only what students will actually use.
            </p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
            <span style={{
              fontSize: 12, fontWeight: 800, padding: "7px 14px", borderRadius: 999,
              background: enabledCount ? headerTone.soft : C.surfaceAlt,
              color: enabledCount ? headerTone.chip : C.muted,
              border: `1.5px solid ${enabledCount ? `${headerTone.accent}44` : C.border}`,
            }}>
              {enabledCount} bank{enabledCount === 1 ? "" : "s"} on
            </span>
            {meta && (
              <span style={{ fontSize: 11, color: C.faint, fontWeight: 650 }}>
                Seed · {meta.pmCount} PM · {meta.leetcodeCount} LC · {meta.caseCount} cases
              </span>
            )}
          </div>
        </div>
      </div>

      {BANK_GROUPS.map((group) => {
        const gt = FIELD_TONES[group.tone] || FIELD_TONES.blue;
        const onCount = group.items.filter((i) => banks[i.key]).length;
        return (
          <FieldGroup
            key={group.title}
            title={group.title}
            icon={group.icon}
            tone={group.tone}
            style={{ marginTop: group === BANK_GROUPS[0] ? 0 : 14 }}
          >
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              gap: 10, marginBottom: 12, flexWrap: "wrap",
            }}>
              <p style={{ fontSize: 12, color: C.muted, margin: 0, lineHeight: 1.4 }}>{group.hint}</p>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <button
                  type="button"
                  onClick={() => setAllInGroup(group.items, true)}
                  style={{
                    fontSize: 11, fontWeight: 750, padding: "5px 11px", borderRadius: 9,
                    border: `1.5px solid ${C.success}44`, background: C.successBg, color: C.success, cursor: "pointer",
                  }}
                >
                  All on
                </button>
                <button
                  type="button"
                  onClick={() => setAllInGroup(group.items, false)}
                  style={{
                    fontSize: 11, fontWeight: 750, padding: "5px 11px", borderRadius: 9,
                    border: `1.5px solid ${C.error}33`, background: C.errorBg, color: C.error, cursor: "pointer",
                  }}
                >
                  All off
                </button>
                <span style={{
                  fontSize: 11, fontWeight: 800, color: gt.chip,
                  padding: "4px 10px", borderRadius: 999, background: gt.soft,
                  border: `1px solid ${gt.accent}33`,
                }}>
                  {onCount}/{group.items.length}
                </span>
              </div>
            </div>

            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
              gap: 10,
            }}>
              {group.items.map((b) => {
                const on = !!banks[b.key];
                const t = FIELD_TONES[b.tone] || FIELD_TONES.blue;
                return (
                  <button
                    key={b.key}
                    type="button"
                    onClick={() => toggleBank(b.key)}
                    aria-pressed={on}
                    style={{
                      textAlign: "left",
                      padding: "14px 14px 12px",
                      borderRadius: 14,
                      border: `1.5px solid ${on ? `${t.accent}55` : C.border}`,
                      background: on
                        ? `linear-gradient(145deg, ${t.soft} 0%, #FFFFFF 80%)`
                        : "#fff",
                      boxShadow: on ? `inset 3px 0 0 ${t.accent}, 0 6px 16px ${t.accent}12` : "none",
                      cursor: "pointer",
                      transition: "border-color .15s, background .15s, box-shadow .15s",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
                      <span style={{
                        width: 30, height: 30, borderRadius: 9, flexShrink: 0,
                        background: on ? t.accent : t.soft,
                        color: on ? "#fff" : t.accent,
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        border: `1px solid ${t.accent}44`,
                      }}>
                        <i className={`ti ${b.icon}`} style={{ fontSize: 14 }} />
                      </span>
                      <span style={{
                        flexShrink: 0,
                        minWidth: 40, height: 24, borderRadius: 999, padding: "0 10px",
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        fontSize: 10, fontWeight: 800, letterSpacing: "0.04em",
                        background: on ? t.accent : C.surfaceAlt,
                        color: on ? "#fff" : C.faint,
                        border: on ? "none" : `1px solid ${C.border}`,
                      }}>
                        {on ? "ON" : "OFF"}
                      </span>
                    </div>
                    <div style={{ fontWeight: 800, fontSize: 13.5, color: on ? t.chip : C.text, letterSpacing: "-0.01em", marginBottom: 4 }}>
                      {b.label}
                    </div>
                    <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.45 }}>{b.desc}</div>
                  </button>
                );
              })}
            </div>
          </FieldGroup>
        );
      })}

      {(banks.analyticsVidhya || banks.agentWorkbench) && (
        <FieldGroup title="Platform notes" icon="ti-notes" tone="slate" style={{ marginTop: 14 }}>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: 12,
          }}>
            {banks.analyticsVidhya && (
              <FancyTextarea
                tone="green"
                icon="ti-chart-dots"
                label="Analytics Vidhya notes"
                value={config.avCourseNotes || ""}
                onChange={(e) => setField("avCourseNotes", e.target.value)}
                placeholder="e.g. AV NLP weeks 1–2 — videos + quiz"
                rows={2}
                style={{ minHeight: 72 }}
              />
            )}
            {banks.agentWorkbench && (
              <FancyTextarea
                tone="amber"
                icon="ti-robot"
                label="Agent Workbench guidance"
                value={config.agentWorkbenchNotes || ""}
                onChange={(e) => setField("agentWorkbenchNotes", e.target.value)}
                placeholder="Optional faculty note — e.g. use manufacturing sample JSON for today's webhook agent"
                rows={2}
                style={{ minHeight: 72 }}
              />
            )}
          </div>
        </FieldGroup>
      )}

      <label
        htmlFor="enrichLinks"
        style={{
          display: "flex", alignItems: "flex-start", gap: 12, marginTop: 14, marginBottom: 14,
          padding: "14px 16px", borderRadius: 14,
          background: config.enrichFromLinks
            ? `linear-gradient(135deg, ${FIELD_TONES.amber.soft} 0%, #FFFFFF 80%)`
            : C.surfaceAlt,
          border: `1.5px solid ${config.enrichFromLinks ? `${FIELD_TONES.amber.accent}55` : C.border}`,
          boxShadow: config.enrichFromLinks ? `inset 3px 0 0 ${FIELD_TONES.amber.accent}` : "none",
          cursor: "pointer",
        }}
      >
        <input
          id="enrichLinks"
          type="checkbox"
          checked={!!config.enrichFromLinks}
          onChange={(e) => setField("enrichFromLinks", e.target.checked)}
          style={{ marginTop: 3, width: 16, height: 16, accentColor: FIELD_TONES.amber.accent }}
        />
        <span>
          <span style={{ display: "block", fontSize: 13, fontWeight: 800, color: config.enrichFromLinks ? FIELD_TONES.amber.chip : C.text }}>
            Enrich from uploads / links
          </span>
          <span style={{ display: "block", fontSize: 12, color: C.muted, marginTop: 2, lineHeight: 1.45 }}>
            Use uploaded PDFs or pasted case links when generating. Never invent sources.
          </span>
        </span>
      </label>

      <FieldGroup title="Extra assessment files" icon="ti-upload" tone="slate" style={{ marginTop: 0 }}>
        <p style={{ fontSize: 12, color: C.muted, margin: "0 0 10px" }}>
          PDF / Excel — extract as TXT or CSV if needed
        </p>
        <UploadZone paramKey="assessmentBank" files={files} setFiles={setFiles} />
      </FieldGroup>

      <button
        type="button"
        onClick={() => setHelpOpen((v) => !v)}
        style={{
          marginTop: 12, border: `1.5px solid ${FIELD_TONES.blue.accent}33`,
          background: FIELD_TONES.blue.soft, borderRadius: 10,
          color: FIELD_TONES.blue.chip, fontSize: 12.5, fontWeight: 800,
          cursor: "pointer", padding: "8px 14px",
        }}
      >
        {helpOpen ? "Hide schedule mapping" : "How this feeds the daily schedule"}
      </button>
      {helpOpen && (
        <div style={{
          marginTop: 10, padding: "14px 16px", borderRadius: 14,
          background: `linear-gradient(135deg, ${FIELD_TONES.blue.soft} 0%, #FFFFFF 80%)`,
          border: `1.5px solid ${FIELD_TONES.blue.accent}33`,
          boxShadow: `inset 3px 0 0 ${FIELD_TONES.blue.accent}`,
        }}>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: C.text, lineHeight: 1.7 }}>
            <li><strong>Cases</strong> — each teammate gets a different case the same day</li>
            <li><strong>Product speak</strong> — brand of the day (e.g. Haldirams) in Speak slot</li>
            <li><strong>Refresh game</strong> — Hollywood / Pictionary energy break</li>
            <li><strong>Project breakdown</strong> — shapes the learn→implement pipeline</li>
            <li><strong>Company</strong> — short Placement Micro, not a long block</li>
            <li><strong>AV / Workbench / Certs / IAE</strong> — guided slots when enabled</li>
          </ul>
        </div>
      )}
    </div>
  );
}
